// src/pages/student/CoverLetterGenerator.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  FaFileAlt,
  FaPaperPlane,
  FaSpinner,
  FaCheckCircle,
  FaInfoCircle,
  FaDownload,
  FaExclamationTriangle,
  FaLock,
  FaSave,
  FaEdit,
  FaRobot,
} from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type AccessInfo = {
  hasAccess: boolean;
  hasAusbildungAccess: boolean;
  hasFSJAccess: boolean;
  studentId: number;
  studentName: string;
  usageCount: number;
  maxGenerations: number | null;
  remainingGenerations: number | null;
  lastUsedAt: string | null;
  usageCountFSJ: number;
  maxGenerationsFSJ: number | null;
  remainingGenerationsFSJ: number | null;
};

type BriefkopfData = {
  name: string;
  address: string;
  phone: string;
  mobile: string;
  email: string;
};


const CoverLetterGenerator: React.FC = () => {
  const { student } = useAuth();
  const navigate = useNavigate();

  // Access control
  const [accessInfo, setAccessInfo] = useState<AccessInfo | null>(null);
  const [checkingAccess, setCheckingAccess] = useState(true);

  // Briefkopf (saved in localStorage)
  const [briefkopfSaved, setBriefkopfSaved] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");

  // Interest & Background (saved in localStorage)
  const [profileSaved, setProfileSaved] = useState(false);
  const [interest, setInterest] = useState("");
  const [background, setBackground] = useState("");

  // Circular
  const [circular, setCircular] = useState("");

  // Company info (extracted by AI)
  const [extracting, setExtracting] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [street, setStreet] = useState("");
  const [plz, setPlz] = useState("");
  const [city, setCity] = useState("");
  const [betreff, setBetreff] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [generatedText, setGeneratedText] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [fileName, setFileName] = useState("");

  // Tab
  const [activeTab, setActiveTab] = useState<"ausbildung" | "fsj">("ausbildung");

  // FSJ-specific state
  const [fsjCircular, setFsjCircular] = useState("");
  const [fsjServiceType, setFsjServiceType] = useState<"FSJ" | "BFD" | "FÖJ">("FSJ");
  const [fsjTraeger, setFsjTraeger] = useState("");
  const [fsjEinsatzstelle, setFsjEinsatzstelle] = useState("");
  const [fsjContactPerson, setFsjContactPerson] = useState("");
  const [fsjStreet, setFsjStreet] = useState("");
  const [fsjPlz, setFsjPlz] = useState("");
  const [fsjCity, setFsjCity] = useState("");
  const [fsjBetreff, setFsjBetreff] = useState("");
  const [fsjReferenceNumber, setFsjReferenceNumber] = useState("");
  const [fsjExtracting, setFsjExtracting] = useState(false);
  const [fsjGeneratedText, setFsjGeneratedText] = useState("");
  const [fsjWordCount, setFsjWordCount] = useState(0);
  const [fsjFileName, setFsjFileName] = useState("");

  // Load from localStorage on mount
  useEffect(() => {
    checkAccess();
    loadFromLocalStorage();
  }, []);

  const checkAccess = async () => {
    setCheckingAccess(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/student/cover-letter/check-access`,
        {
          credentials: "include",
        }
      );

      if (res.ok) {
        const data: AccessInfo = await res.json();
        setAccessInfo(data);
        // Set default tab based on granted permissions
        if (!data.hasAusbildungAccess && data.hasFSJAccess) {
          setActiveTab("fsj");
        } else {
          setActiveTab("ausbildung");
        }
      } else {
        setError("Failed to check access");
      }
    } catch (e) {
      setError("Network error. Please try again.");
    } finally {
      setCheckingAccess(false);
    }
  };

  const loadFromLocalStorage = () => {
    // Load Briefkopf
    const savedBriefkopf = localStorage.getItem("coverLetter_briefkopf");
    if (savedBriefkopf) {
      try {
        const data: BriefkopfData = JSON.parse(savedBriefkopf);
        setName(data.name || "");
        setAddress(data.address || "");
        setPhone(data.phone || "");
        setMobile(data.mobile || "");
        setEmail(data.email || "");
        setBriefkopfSaved(true);
      } catch (e) {
        console.error("Failed to load briefkopf from localStorage", e);
      }
    } else if (student) {
      // Pre-fill from student account if no saved data
      setName(`${student.firstName} ${student.lastName}`);
      setEmail(student.email || "");
      setMobile(student.whatsappNumber?.toString() || "");
    }

    // Load Interest & Background
    const savedInterest = localStorage.getItem("coverLetter_interest");
    const savedBackground = localStorage.getItem("coverLetter_background");

    if (savedInterest) {
      setInterest(savedInterest);
      setProfileSaved(true);
    }
    if (savedBackground) {
      setBackground(savedBackground);
      setProfileSaved(true);
    }
  };

  const saveBriefkopf = () => {
    if (!name || !mobile || !email) {
      setError("Please fill in required fields: Name, Mobile, Email");
      return;
    }

    const data: BriefkopfData = {
      name: name.trim(),
      address: address.trim(),
      phone: phone.trim(),
      mobile: mobile.trim(),
      email: email.trim(),
    };

    localStorage.setItem("coverLetter_briefkopf", JSON.stringify(data));
    setBriefkopfSaved(true);
    setSuccess("✅ Personal information saved to browser!");

    setTimeout(() => setSuccess(""), 3000);
  };

  const saveProfile = () => {
    if (!interest.trim() || !background.trim()) {
      setError("Please fill in both Interest and Background fields");
      return;
    }

    localStorage.setItem("coverLetter_interest", interest.trim());
    localStorage.setItem("coverLetter_background", background.trim());
    setProfileSaved(true);
    setSuccess("✅ Your profile saved to browser!");

    setTimeout(() => setSuccess(""), 3000);
  };

  const resetBriefkopf = () => {
    if (
      window.confirm(
        "Are you sure you want to clear saved personal information?"
      )
    ) {
      localStorage.removeItem("coverLetter_briefkopf");
      setName("");
      setAddress("");
      setPhone("");
      setMobile("");
      setEmail("");
      setBriefkopfSaved(false);
      setSuccess("Personal information cleared");
    }
  };

  const resetProfile = () => {
    if (window.confirm("Are you sure you want to clear saved profile?")) {
      localStorage.removeItem("coverLetter_interest");
      localStorage.removeItem("coverLetter_background");
      setInterest("");
      setBackground("");
      setProfileSaved(false);
      setSuccess("Profile cleared");
    }
  };

  const extractCompanyInfo = async () => {
    if (!circular.trim()) {
      setError("Please paste the job circular first");
      return;
    }

    setExtracting(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/cover-letter/extract-company-info`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ circular: circular.trim() }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setCompanyName(data.companyName || "");
        setContactPerson(data.contactPerson || "");
        setStreet(data.street || "");
        setPlz(data.plz || "");
        setCity(data.city || "");
        setBetreff(data.betreff || "");
        setSuccess(
          "✅ Company information extracted! Please verify and edit if needed."
        );
      } else {
        setError("Failed to extract company info. Please fill manually.");
      }
    } catch (err) {
      console.error("Error extracting company info:", err);
      setError("Network error. Please fill company information manually.");
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setGeneratedText("");

    // Validation
    if (!circular.trim()) {
      setError("Please provide the job circular");
      return;
    }
    if (!interest.trim() || !background.trim()) {
      setError("Please fill in your Interest and Background");
      return;
    }
    if (!name || !mobile || !email) {
      setError("Please fill in required personal information");
      return;
    }
    if (!companyName || !betreff) {
      setError("Please provide company name and subject (Betreff)");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        // Circular
        circular: circular.trim(),

        // Briefkopf
        name: name.trim(),
        address: address.trim(),
        phone: phone.trim(),
        mobile: mobile.trim(),
        email: email.trim(),

        // Interest & Background
        interest: interest.trim(),
        background: background.trim(),

        // Company Info
        companyName: companyName.trim(),
        contactPerson: contactPerson.trim(),
        street: street.trim(),
        plz: plz.trim(),
        city: city.trim(),
        betreff: betreff.trim(),
        referenceNumber: referenceNumber.trim(),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/student/cover-letter/generate`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setGeneratedText(data.coverLetterText);
        setWordCount(data.wordCount);
        setFileName(data.fileName);
        setSuccess("Cover letter generated successfully!");

        // Refresh access info
        checkAccess();

        // Scroll to result
        setTimeout(() => {
          document.getElementById("result-section")?.scrollIntoView({
            behavior: "smooth",
          });
        }, 100);
      } else {
        setError(data.message || "Failed to generate cover letter");
      }
    } catch (err) {
      console.error("Error generating cover letter:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!generatedText) return;

    setLoading(true);
    try {
      const payload = {
        circular: circular.trim(),
        name: name.trim(),
        address: address.trim(),
        phone: phone.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
        interest: interest.trim(),
        background: background.trim(),
        companyName: companyName.trim(),
        contactPerson: contactPerson.trim(),
        street: street.trim(),
        plz: plz.trim(),
        city: city.trim(),
        betreff: betreff.trim(),
        referenceNumber: referenceNumber.trim(),
        coverLetterText: generatedText, // IMPORTANT!
      };

      const response = await fetch(
        `${API_BASE_URL}/api/student/cover-letter/generate-download`, // ← CHECK THIS!
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || "Bewerbungsschreiben.docx";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setSuccess("Cover letter downloaded successfully!");
      } else {
        setError("Failed to download cover letter");
      }
    } catch (err) {
      setError("Download failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCircular("");
    setCompanyName("");
    setContactPerson("");
    setStreet("");
    setPlz("");
    setCity("");
    setBetreff("");
    setReferenceNumber("");
    setGeneratedText("");
    setError("");
    setSuccess("");
  };

  const extractFSJInfo = async () => {
    const text = fsjCircular.trim() || (fsjTraeger + " " + fsjEinsatzstelle).trim();
    if (!text) {
      setError("Please paste the FSJ circular or enter the Träger/Einsatzstelle name");
      return;
    }
    setFsjExtracting(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/cover-letter/extract-fsj-info`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ circular: text }),
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        if (data.serviceType) setFsjServiceType(data.serviceType as "FSJ" | "BFD" | "FÖJ");
        if (data.traegerName) setFsjTraeger(data.traegerName);
        if (data.einsatzstelleName) setFsjEinsatzstelle(data.einsatzstelleName);
        if (data.contactPerson) setFsjContactPerson(data.contactPerson);
        if (data.street) setFsjStreet(data.street);
        if (data.plz) setFsjPlz(data.plz);
        if (data.city) setFsjCity(data.city);
        if (data.betreff) setFsjBetreff(data.betreff);
        setSuccess("✅ Organization info extracted! Please verify and edit if needed.");
      } else {
        setError("Failed to extract info. Please fill manually.");
      }
    } catch {
      setError("Network error. Please fill organization information manually.");
    } finally {
      setFsjExtracting(false);
    }
  };

  const handleFSJSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setFsjGeneratedText("");

    if (!interest.trim() || !background.trim()) {
      setError("Please fill in your Interest and Background");
      return;
    }
    if (!name || !mobile || !email) {
      setError("Please fill in required personal information");
      return;
    }
    if (!fsjTraeger && !fsjEinsatzstelle) {
      setError("Please provide at least the Träger or Einsatzstelle name");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        serviceType: fsjServiceType,
        circular: fsjCircular.trim(),
        name: name.trim(),
        address: address.trim(),
        phone: phone.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
        interest: interest.trim(),
        background: background.trim(),
        traegerName: fsjTraeger.trim(),
        einsatzstelleName: fsjEinsatzstelle.trim(),
        contactPerson: fsjContactPerson.trim(),
        street: fsjStreet.trim(),
        plz: fsjPlz.trim(),
        city: fsjCity.trim(),
        betreff: fsjBetreff.trim(),
        referenceNumber: fsjReferenceNumber.trim(),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/student/cover-letter/generate-fsj`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setFsjGeneratedText(data.coverLetterText);
        setFsjWordCount(data.wordCount);
        setFsjFileName(data.fileName);
        setSuccess("FSJ cover letter generated successfully!");
        checkAccess();
        setTimeout(() => {
          document.getElementById("fsj-result-section")?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        setError(data.message || "Failed to generate cover letter");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFSJDownload = async () => {
    if (!fsjGeneratedText) return;
    setLoading(true);
    try {
      const payload = {
        serviceType: fsjServiceType,
        circular: fsjCircular.trim(),
        name: name.trim(),
        address: address.trim(),
        phone: phone.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
        interest: interest.trim(),
        background: background.trim(),
        traegerName: fsjTraeger.trim(),
        einsatzstelleName: fsjEinsatzstelle.trim(),
        contactPerson: fsjContactPerson.trim(),
        street: fsjStreet.trim(),
        plz: fsjPlz.trim(),
        city: fsjCity.trim(),
        betreff: fsjBetreff.trim(),
        referenceNumber: fsjReferenceNumber.trim(),
        coverLetterText: fsjGeneratedText,
      };
      const response = await fetch(
        `${API_BASE_URL}/api/student/cover-letter/generate-download-fsj`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fsjFileName || `Bewerbungsschreiben_${fsjServiceType}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess("Cover letter downloaded successfully!");
      } else {
        setError("Failed to download cover letter");
      }
    } catch {
      setError("Download failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFSJReset = () => {
    setFsjCircular("");
    setFsjTraeger("");
    setFsjEinsatzstelle("");
    setFsjContactPerson("");
    setFsjStreet("");
    setFsjPlz("");
    setFsjCity("");
    setFsjBetreff("");
    setFsjReferenceNumber("");
    setFsjGeneratedText("");
    setError("");
    setSuccess("");
  };

  // Loading access check
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-[#009fe3] flex items-center gap-3">
          <FaSpinner className="animate-spin text-2xl" />
          <span className="text-lg">Checking access...</span>
        </div>
      </div>
    );
  }

  // No access — neither Ausbildung nor FSJ granted
  if (!accessInfo?.hasAusbildungAccess && !accessInfo?.hasFSJAccess) {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader
          icon={<FaFileAlt />}
          title="Cover Letter Generator"
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: "Dashboard", to: "/student/student-dashboard" },
            { label: "Cover Letter Generator" },
          ]}
        />

        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaLock className="text-red-600 text-3xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Access Restricted
            </h2>
            <p className="text-gray-600 mb-6">
              You don't have access to the Cover Letter Generator. Please
              contact the administrator to request access.
            </p>
            <button
              onClick={() => navigate("/student/student-dashboard")}
              className="px-6 py-3 bg-[#009fe3] hover:bg-[#0088cc] text-white rounded-lg font-semibold transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Has access - show generator
  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaFileAlt />}
        title="Cover Letter Generator"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Cover Letter Generator" },
        ]}
      />

      {/* Usage Badge toolbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 flex justify-end">
        <div className="bg-white rounded-lg px-4 py-2 text-gray-700 shadow-sm border border-gray-200">
          <div className="text-xs font-medium text-gray-500">Usage</div>
          <div className="text-lg font-bold text-gray-900">
            {accessInfo.usageCount}
            {accessInfo.maxGenerations && ` / ${accessInfo.maxGenerations}`}
          </div>
          {accessInfo.remainingGenerations !== null && (
            <div className="text-xs text-gray-500">
              {accessInfo.remainingGenerations} remaining
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Instructions */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 mb-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FaInfoCircle className="text-blue-600" />
            How to Use (কিভাবে ব্যবহার করবেন)
          </h2>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">১.</span>
              <span>
                Fill your personal information (saved in browser for future use)
                ✓
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">২.</span>
              <span>
                Save your interest and background (one-time, editable later) ✓
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">৩.</span>
              <span>
                Paste job circular → AI extracts company info automatically 🤖
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">৪.</span>
              <span>
                Generate professional German cover letter (180-200 words)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">৫.</span>
              <span>Download DOCX file ⬇️</span>
            </li>
          </ul>
          <div className="mt-4 bg-white/50 rounded-lg p-3 border border-blue-200">
            <p className="text-sm text-gray-800 flex items-start gap-2">
              <FaCheckCircle className="text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong>বিশেষ বৈশিষ্ট্য:</strong> Your personal info is saved in
                your browser. Next time, just paste the circular and generate!
                AI creates creative openings using keywords from the job
                posting.
              </span>
            </p>
          </div>
        </div>

        {/* Warning about localStorage */}
        <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-800 p-4 mb-6 rounded">
          <p className="text-sm flex items-start gap-2">
            <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
            <span>
              <strong>Important:</strong> Your saved information is stored in
              your browser. Please don't clear your browser cache to keep your
              data.
            </span>
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6 bg-white rounded-xl shadow-md p-2">
          <button
            type="button"
            disabled={!accessInfo.hasAusbildungAccess}
            onClick={() => { if (accessInfo.hasAusbildungAccess) { setActiveTab("ausbildung"); setError(""); setSuccess(""); } }}
            title={!accessInfo.hasAusbildungAccess ? "You don't have access to the Ausbildung generator" : undefined}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
              !accessInfo.hasAusbildungAccess
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : activeTab === "ausbildung"
                ? "bg-[#009fe3] text-white shadow"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            🏢 Ausbildung
            {!accessInfo.hasAusbildungAccess && <FaLock size={11} />}
          </button>
          <button
            type="button"
            disabled={!accessInfo.hasFSJAccess}
            onClick={() => { if (accessInfo.hasFSJAccess) { setActiveTab("fsj"); setError(""); setSuccess(""); } }}
            title={!accessInfo.hasFSJAccess ? "You don't have access to the FSJ/BFD/FÖJ generator" : undefined}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
              !accessInfo.hasFSJAccess
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : activeTab === "fsj"
                ? "bg-[#009fe3] text-white shadow"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            🤝 FSJ / BFD / FÖJ
            {!accessInfo.hasFSJAccess && <FaLock size={11} />}
          </button>
        </div>

        {/* Limit Warning */}
        {accessInfo.remainingGenerations !== null &&
          accessInfo.remainingGenerations <= 2 && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-800 p-4 mb-6 rounded flex items-start gap-3">
              <FaExclamationTriangle className="text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Low Remaining Generations</p>
                <p className="text-sm">
                  You have only {accessInfo.remainingGenerations} generation
                  {accessInfo.remainingGenerations !== 1 ? "s" : ""} left.
                  Please use them wisely.
                </p>
              </div>
            </div>
          )}

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
            <p className="font-semibold">Error</p>
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded flex items-start gap-2">
            <FaCheckCircle className="mt-0.5 flex-shrink-0" />
            <p>{success}</p>
          </div>
        )}

        <form onSubmit={activeTab === "ausbildung" ? handleSubmit : handleFSJSubmit} className="space-y-6">
          {/* SECTION 1: Briefkopf */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FaFileAlt className="text-[#009fe3]" />
                Briefkopf (Personal Header Information)
                {briefkopfSaved && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    ✓ Saved
                  </span>
                )}
              </h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Vollständiger Name (Full Name) *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Max Müller"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Anschrift (Address)
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="e.g., Itakhola, Madhabpur, Habiganj, Bangladesh"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefon/Fax
                  </label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g., +880-123-456789"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mobilnummer (Mobile) *
                  </label>
                  <input
                    type="text"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="e.g., +880-1760000000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  E-Mail-Adresse *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g., max.mueller@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={saveBriefkopf}
                  className="flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
                >
                  <FaSave />
                  Save to Browser
                </button>
                {briefkopfSaved && (
                  <button
                    type="button"
                    onClick={resetBriefkopf}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                  >
                    Clear Saved Data
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 2: Profile (Interest & Background) */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FaEdit className="text-[#009fe3]" />
                Your Profile (Interesse & Hintergrund)
                {profileSaved && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    ✓ Saved
                  </span>
                )}
              </h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Interesse für den Beruf (Interest in the Profession) *
                </label>
                <textarea
                  value={interest}
                  onChange={(e) => setInterest(e.target.value)}
                  placeholder="Write in German why this profession interests you... e.g., Ich interessiere mich für die Arbeit mit Menschen und möchte anderen helfen..."
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent resize-none text-sm"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Write in German about your passion for this career
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Academic Background & Language Skills *
                </label>
                <textarea
                  value={background}
                  onChange={(e) => setBackground(e.target.value)}
                  placeholder={`Education:\n- Higher Secondary Certificate (12. Klasse)\n- Science Background\n\nLanguages:\n- Bengali (Native)\n- English (B2)\n- German (A2, currently learning)\n\nSkills:\n- Computer skills, Teamwork...`}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent resize-none text-sm font-mono"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  📚 List your education, language skills, and relevant
                  abilities
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={saveProfile}
                  className="flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
                >
                  <FaSave />
                  Save Profile
                </button>
                {profileSaved && (
                  <button
                    type="button"
                    onClick={resetProfile}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                  >
                    Clear Profile
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── AUSBILDUNG SECTIONS ── */}
          {activeTab === "ausbildung" && (
            <>
              {/* SECTION 3: Job Circular */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaFileAlt className="text-[#009fe3]" />
                  Ausbildung Circular (Job Posting) *
                </h3>
                <textarea
                  value={circular}
                  onChange={(e) => setCircular(e.target.value)}
                  placeholder="Paste the complete Ausbildung job circular here..."
                  rows={10}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent resize-none text-sm"
                  required
                />
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-gray-500">{circular.length} characters</div>
                  <button
                    type="button"
                    onClick={extractCompanyInfo}
                    disabled={extracting || !circular.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {extracting ? <><FaSpinner className="animate-spin" />Extracting...</> : <><FaRobot />Extract Company Info</>}
                  </button>
                </div>
              </div>

              {/* SECTION 4: Company Information */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Postalische Anschrift (Company Address)</h3>
                <p className="text-sm text-gray-600 mb-4">🤖 Click "AI Extract" above to auto-fill, or enter manually</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Unternehmen (Company Name) *</label>
                    <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g., Wohnungsbaugenossenschaft Süderelbe eG" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" required />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Herrn/Frau Ansprechpartner/-in (Contact Person)</label>
                    <input type="text" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} placeholder="e.g., Herrn Tekin Kurtulus (optional)" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Straße, Postfach (Street/PO Box)</label>
                    <input type="text" value={street} onChange={(e) => setStreet(e.target.value)} placeholder="e.g., Cuxhavener Str. 410" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">PLZ (Postal Code)</label>
                      <input type="text" value={plz} onChange={(e) => setPlz(e.target.value)} placeholder="e.g., 21149" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Ort (City)</label>
                      <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g., Hamburg" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 5: Betreff */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Betreff (Subject Line)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Betreff *</label>
                    <input type="text" value={betreff} onChange={(e) => setBetreff(e.target.value)} placeholder="e.g., Bewerbung: Ausbildung zur Pflegefachfrau (m/w/d)" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" required />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Referenz-/Aktenzeichen (Reference Number) - Optional</label>
                    <input type="text" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="e.g., REF-2024-PF-123 (if provided in circular)" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-800 p-4 rounded-lg flex items-start gap-3">
                <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm">
                  <strong>Important:</strong> The generated cover letter is approximately <strong>80% ready</strong>. You must review it thoroughly — check all facts, German phrasing, and personal details before sending.
                </p>
              </div>
              <div className="flex gap-4">
                <button type="submit" disabled={loading || accessInfo.remainingGenerations === 0} className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:-translate-y-0.5 disabled:transform-none text-lg">
                  {loading ? <><FaSpinner className="animate-spin" />Generating...</> : <><FaPaperPlane />Generate Cover Letter</>}
                </button>
                <button type="button" onClick={handleReset} disabled={loading} className="px-6 py-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors">Reset Form</button>
              </div>
            </>
          )}

          {/* ── FSJ / BFD / FÖJ SECTIONS ── */}
          {activeTab === "fsj" && (
            <>
              {/* Service Type */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaFileAlt className="text-[#009fe3]" />
                  Freiwilligendienst Type *
                </h3>
                <div className="flex gap-3">
                  {(["FSJ", "BFD", "FÖJ"] as const).map((type) => (
                    <button key={type} type="button" onClick={() => setFsjServiceType(type)}
                      className={`flex-1 py-3 rounded-lg font-bold text-sm border-2 transition-all ${fsjServiceType === type ? "border-[#009fe3] bg-[#009fe3] text-white" : "border-gray-300 text-gray-600 hover:border-[#009fe3]"}`}>
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* SECTION 3: Circular */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <FaFileAlt className="text-[#009fe3]" />
                  {fsjServiceType} Circular or Organization Name
                </h3>
                <p className="text-sm text-gray-500 mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  💡 <strong>Tip:</strong> Paste the full circular if available. If the circular only shows the Träger/Einsatzstelle name — that's fine! Our AI will research the organization and write specific, relevant content.
                </p>
                <textarea
                  value={fsjCircular}
                  onChange={(e) => setFsjCircular(e.target.value)}
                  placeholder={`Paste the ${fsjServiceType} circular here, or just the organization name/description...\n\nExample (sparse):\nTräger: Schleswiger Werk- und Wohnstätten e.V.\nEinsatzstelle: Bioland-Bauernhof Altenholz\n\nExample (full circular):\n[Paste complete circular text]`}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent resize-none text-sm"
                />
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-gray-500">{fsjCircular.length} characters</div>
                  <button type="button" onClick={extractFSJInfo} disabled={fsjExtracting}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {fsjExtracting ? <><FaSpinner className="animate-spin" />Extracting...</> : <><FaRobot />Extract Info</>}
                  </button>
                </div>
              </div>

              {/* SECTION 4: Organization Info */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Organisation (Träger &amp; Einsatzstelle)</h3>
                <p className="text-sm text-gray-600 mb-4">🤖 Click "Extract Info" above or fill manually</p>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Träger (Sponsoring Org) *</label>
                      <input type="text" value={fsjTraeger} onChange={(e) => setFsjTraeger(e.target.value)}
                        placeholder="e.g., Schleswiger Werk- und Wohnstätten e.V." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Einsatzstelle (Placement Site)</label>
                      <input type="text" value={fsjEinsatzstelle} onChange={(e) => setFsjEinsatzstelle(e.target.value)}
                        placeholder="e.g., Bioland-Bauernhof Altenholz (if different from Träger)" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Ansprechpartner/-in (Contact Person)</label>
                    <input type="text" value={fsjContactPerson} onChange={(e) => setFsjContactPerson(e.target.value)}
                      placeholder="e.g., Frau Busche (optional)" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Straße (Street)</label>
                    <input type="text" value={fsjStreet} onChange={(e) => setFsjStreet(e.target.value)}
                      placeholder="e.g., Musterstraße 12" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">PLZ</label>
                      <input type="text" value={fsjPlz} onChange={(e) => setFsjPlz(e.target.value)}
                        placeholder="e.g., 24837" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Ort (City)</label>
                      <input type="text" value={fsjCity} onChange={(e) => setFsjCity(e.target.value)}
                        placeholder="e.g., Schleswig" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 5: Betreff */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Betreff (Subject Line)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Betreff *</label>
                    <input type="text" value={fsjBetreff} onChange={(e) => setFsjBetreff(e.target.value)}
                      placeholder={`e.g., Bewerbung um einen ${fsjServiceType}-Platz bei Schleswiger Werk- und Wohnstätten`}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                    <p className="text-xs text-gray-500 mt-1">Auto-filled by AI extract. Format: "Bewerbung um einen {fsjServiceType}-Platz bei [Einsatzstelle]"</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Referenz-/Aktenzeichen - Optional</label>
                    <input type="text" value={fsjReferenceNumber} onChange={(e) => setFsjReferenceNumber(e.target.value)}
                      placeholder="Reference number if provided" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" />
                  </div>
                </div>
              </div>

              {/* FSJ Action Buttons */}
              <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-800 p-4 rounded-lg flex items-start gap-3">
                <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm">
                  <strong>Important:</strong> The generated cover letter is approximately <strong>80% ready</strong>. You must review it thoroughly — check all facts, German phrasing, and personal details before sending.
                </p>
              </div>
              <div className="flex gap-4">
                <button type="submit" disabled={loading || accessInfo.remainingGenerations === 0}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:-translate-y-0.5 disabled:transform-none text-lg">
                  {loading ? <><FaSpinner className="animate-spin" />Generating...</> : <><FaPaperPlane />Generate {fsjServiceType} Cover Letter</>}
                </button>
                <button type="button" onClick={handleFSJReset} disabled={loading}
                  className="px-6 py-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors">Reset Form</button>
              </div>
            </>
          )}
        </form>

        {/* Ausbildung Generated Result */}
        {activeTab === "ausbildung" && generatedText && (
          <div id="result-section" className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FaCheckCircle className="text-green-600" />
                Generated Cover Letter
              </h3>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  Word Count: <span className={`font-bold ${wordCount >= 180 && wordCount <= 230 ? "text-green-600" : "text-amber-600"}`}>{wordCount}</span>
                  {wordCount >= 180 && wordCount <= 230 && " ✅"}
                </div>
                <button onClick={handleDownload} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50">
                  <FaDownload />Download DOCX
                </button>
              </div>
            </div>
            <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-800 p-4 mb-4 rounded-lg flex items-start gap-3">
              <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm">⚠️ Review Before Using (গুরুত্বপূর্ণ)</p>
                <p className="text-sm mt-1">
                  This cover letter is approximately <strong>80% ready</strong>. Please review it thoroughly before sending —
                  check all personal facts, verify German phrasing, and adjust any details that don't match your situation.
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="prose prose-sm max-w-none" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
                {generatedText.split("\n").map((line, index) =>
                  line.trim() ? <p key={index} className="mb-4 text-gray-800">{line}</p> : <br key={index} />
                )}
              </div>
            </div>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 flex items-start gap-2">
                <FaInfoCircle className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span>
                  This is the body text of your cover letter. The complete DOCX
                  file includes proper DIN 5008 formatting, sender/recipient
                  addresses, date, subject line, greeting, and signature.
                </span>
              </p>
            </div>
          </div>
        )}

        {/* FSJ Generated Result */}
        {activeTab === "fsj" && fsjGeneratedText && (
          <div id="fsj-result-section" className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FaCheckCircle className="text-green-600" />
                Generated {fsjServiceType} Cover Letter
              </h3>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  Word Count:{" "}
                  <span className={`font-bold ${fsjWordCount >= 180 && fsjWordCount <= 230 ? "text-green-600" : "text-amber-600"}`}>
                    {fsjWordCount}
                  </span>
                  {fsjWordCount >= 180 && fsjWordCount <= 230 && " ✅"}
                </div>
                <button onClick={handleFSJDownload} disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50">
                  <FaDownload />Download DOCX
                </button>
              </div>
            </div>
            <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-800 p-4 mb-4 rounded-lg flex items-start gap-3">
              <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm">⚠️ Review Before Using (গুরুত্বপূর্ণ)</p>
                <p className="text-sm mt-1">
                  This cover letter is approximately <strong>80% ready</strong>. Please review it thoroughly before sending —
                  check all personal facts, verify German phrasing, and adjust any details that don't match your situation.
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="prose prose-sm max-w-none" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
                {fsjGeneratedText.split("\n").map((line, index) =>
                  line.trim() ? <p key={index} className="mb-4 text-gray-800">{line}</p> : <br key={index} />
                )}
              </div>
            </div>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 flex items-start gap-2">
                <FaInfoCircle className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span>
                  This is the body text of your {fsjServiceType} cover letter. The complete DOCX
                  file includes proper DIN 5008 formatting, sender/recipient addresses, date, subject line, greeting, and signature.
                </span>
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CoverLetterGenerator;
